import { state, getActiveSettings, getTeams, countedRoundsFor, saveToLocalStorage } from '../state.js';
import { formatPlayerName, escapeHtml, playerBirthYear } from '../utils/formatting.js';
import { isYouthTournament } from '../utils/player-ratings.js';
import { printResultSheet } from './result-sheet-print.js';
import { crosstablePrintHtml } from './crosstable.js';
import { prizesPrintHtml } from './prizes.js';
import { roundWhenLabel } from './schedule.js';

// How Print Pairings prints: the ordinary list, or the tick-box sheet that the phone
// page under Scan Results can read. Stored on the tournament as `pairingPrintStyle`
// once the arbiter ticks "use this for the rest of the tournament"; unset means ask.
export const PAIRING_PRINT_STYLES = ['normal', 'tickbox'];

// A tick-box sheet is for results not played yet, so it is offered only for the
// latest round, and not for a team round (its board numbers run per match). It also
// needs a signed-in arbiter: its corner codes carry the scan session, which lives in
// the cloud.
function tickBoxSheetAvailable() {
    const latest = state.rounds[state.rounds.length - 1];
    if (!latest || state.viewedRoundIndex < state.rounds.length - 1) return false;
    if (Array.isArray(latest.matches) && latest.matches.length > 0) return false;
    return !!(window.authApp && window.authApp.isAuthenticated());
}

// Resolves to 'normal', 'tickbox', or null when the arbiter cancels.
async function choosePairingPrintStyle() {
    if (!tickBoxSheetAvailable()) return 'normal';
    const t = state.activeTournament;
    if (PAIRING_PRINT_STYLES.includes(t.pairingPrintStyle)) return t.pairingPrintStyle;

    const picked = await window.Dialog.choose(
        tr('A tick-box sheet lets players mark each result with an X, and a phone reads a photo of it on the Scan Results page.'),
        tr('Print Pairings'),
        [
            { text: tr('Normal pairing list'), value: 'normal', variant: 'primary' },
            { text: tr('With tick boxes for scanning'), value: 'tickbox', variant: 'primary' },
            { text: tr('Cancel'), value: null, variant: 'outline' }
        ],
        { toggleLabel: tr('Use this setting for the rest of the tournament') }
    );
    if (!picked) return null;
    if (picked.toggled) {
        t.pairingPrintStyle = picked.value;
        saveToLocalStorage();
    }
    return picked.value;
}

async function printTickBoxPairings() {
    let sheet;
    try {
        sheet = await window.app.prepareResultSheet();
    } catch (e) {
        await window.Dialog.error(tr('Could not prepare the tick-box sheet:') + ' ' + e.message, tr('Error'));
        return;
    }
    if (!sheet || !printResultSheet(sheet)) {
        await window.Dialog.warn(tr('There are no boards to print a result sheet for.'), tr('Nothing to Print'));
    }
}

/**
 * Everything SwissEngine.calculateStandings needs to rank the field the same way the
 * standings screen does — read from the ACTIVE GROUP's settings, because a grouped
 * tournament gives each group its own tie-breaks, round count and format.
 *
 * These printouts used to pass the point system and nothing else, so they ranked by the
 * default Buchholz tie-breaks no matter what the tournament was actually configured with.
 * A printed standings list could therefore be in a different order from the one on screen
 * — and the printed one is what gets pinned to the wall.
 */
function rankingArgs() {
    const s = getActiveSettings();
    const isRR = ['rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'].includes(s.format);
    return [
        s.pointSystem || '1-0.5-0',
        s.tiebreaks,
        null,   // rating config: these printouts show no rating-change column
        {
            totalRounds: parseInt(s.roundsCount) || state.rounds.length,
            isRoundRobin: isRR,
            countedRounds: countedRoundsFor(s)
        }
    ];
}


export function setupPrintButtons() {
    const btnPrintPlayers = document.getElementById('btn-print-players');
    if (btnPrintPlayers) btnPrintPlayers.addEventListener('click', () => handlePrint('players'));

    const btnPrintPairings = document.getElementById('btn-print-pairings');
    if (btnPrintPairings) btnPrintPairings.addEventListener('click', () => handlePrint('pairings'));

    const btnPrintStandings = document.getElementById('btn-print-standings');
    if (btnPrintStandings) btnPrintStandings.addEventListener('click', () => handlePrint('standings'));

    const btnPrintCrosstable = document.getElementById('btn-print-crosstable');
    if (btnPrintCrosstable) btnPrintCrosstable.addEventListener('click', () => handlePrint('crosstable'));

    const btnPrintPrizes = document.getElementById('btn-print-prizes');
    if (btnPrintPrizes) btnPrintPrizes.addEventListener('click', () => handlePrint('prizes'));

    const btnQuickPrint = document.getElementById('btn-quick-print');

    const showPrintOptions = async () => {
        const choice = await window.Dialog.prompt(tr('Choose which report you would like to print:'), tr('Print Report'), [
            { text: tr('Player List'), value: 'players', variant: 'primary' },
            { text: tr('Pairings'), value: 'pairings', variant: 'primary' },
            { text: tr('Standings'), value: 'standings', variant: 'primary' },
            { text: tr('Crosstable'), value: 'crosstable', variant: 'primary' },
            { text: tr('Prize List'), value: 'prizes', variant: 'primary' }
        ]);
        if (choice) handlePrint(choice);
    };

    if (btnQuickPrint) btnQuickPrint.addEventListener('click', showPrintOptions);
}

/**
 * The two board results as separate cells, the way the printed sheet wants them —
 * one column per side, so the arbiter can write the score in by hand at the board.
 */
function printResultParts(result) {
    if (result === '+/-') return ['1', '0'];
    if (result === '-/+') return ['0', '1'];
    if (result === '-/-') return ['0', '0'];
    if (result && result.includes('-')) {
        const parts = result.split('-');
        return [parts[0], parts[1]];
    }
    return ['', ''];
}

/**
 * The printed pairing sheet for a TEAM round.
 *
 * A separate table per match rather than one long list, because that is how the sheet is
 * used: it is cut up, or read a match at a time, and a board number that runs 1-4 then
 * 5-8 across two different matches is the wrong number for everyone holding it. Each
 * match names its two teams and carries its own board numbers.
 */
function teamPairingsHtml(round) {
    const teams = getTeams();
    const settings = getActiveSettings();
    const nameOf = (id) => {
        const t = teams.find(x => x.id === id);
        return t ? `${t.teamNo}. ${t.name}` : tr('Unknown team');
    };

    return round.matches.map(m => {
        if (m.blackTeam === 'BYE') {
            return `<h3 style="margin: 1rem 0 0.25rem;">${escapeHtml(nameOf(m.whiteTeam))} — ${tr('bye this round')}</h3>`;
        }

        const boards = round.pairings.filter(p => p.matchId === m.id);
        const score = window.TeamEngine
            ? teamMatchScoreText(m, boards, settings)
            : '';

        return `
            <h3 style="margin: 1rem 0 0.25rem;">
                ${escapeHtml(nameOf(m.whiteTeam))} &ndash; ${escapeHtml(nameOf(m.blackTeam))}
                ${score ? `<span style="font-weight: 400;"> ${escapeHtml(score)}</span>` : ''}
            </h3>
            <table class="print-table">
                <thead>
                    <tr>
                        <th style="width: 40pt;">${tr('Board')}</th>
                        <th>${tr('White')}</th>
                        <th style="width: 60pt;" class="text-center">${tr('Result')}</th>
                        <th style="width: 60pt;" class="text-center">${tr('Result')}</th>
                        <th>${tr('Black')}</th>
                    </tr>
                </thead>
                <tbody>
                    ${boards.map(p => {
                        const white = state.players.find(pl => pl.id === p.white);
                        const black = (p.black === 'WO' || p.black === 'BYE')
                            ? { lastName: 'WO', givenName: '', id: 'WO' }
                            : state.players.find(pl => pl.id === p.black);
                        const res = printResultParts(p.result);
                        return `
                            <tr>
                                <td class="text-center font-bold">${p.board}</td>
                                <td>${escapeHtml(formatPlayerName(white))}</td>
                                <td class="text-center font-bold">${res[0] || ''}</td>
                                <td class="text-center font-bold">${res[1] || ''}</td>
                                <td>${escapeHtml(formatPlayerName(black))}</td>
                            </tr>`;
                    }).join('')}
                </tbody>
            </table>`;
    }).join('');
}

/** "2½ - 1½", or '' while nothing has been decided. */
function teamMatchScoreText(match, boards, settings) {
    const half = (n) => {
        const whole = Math.floor(n);
        return (n - whole === 0.5) ? (whole === 0 ? '' : whole) + '½' : String(n);
    };
    let w = 0, b = 0, played = 0;
    boards.forEach(p => {
        if (!p.result) return;
        played++;
        const pts = window.TeamEngine.boardPoints(p.result, settings.pointSystem);
        if (p.whiteTeam === match.whiteTeam) { w += pts.white; b += pts.black; }
        else { w += pts.black; b += pts.white; }
    });
    return played === 0 ? '' : `${half(w)} - ${half(b)}`;
}

/**
 * The printed TEAM standings — the table that actually decides the competition. The
 * individual list is still printed by the 'standings' sheet; this one goes above it.
 */
function teamStandingsHtml() {
    const settings = getActiveSettings();
    const ts = settings.teamSettings;
    const teams = getTeams();
    if (teams.length === 0) return '';

    const table = window.TeamEngine.calculateTeamStandings(teams, state.rounds, {
        boards: ts.boards,
        pointSystem: settings.pointSystem,
        matchPointSystem: ts.matchPointSystem,
        primaryScore: ts.primaryScore,
        tiebreaks: ts.tiebreaks
    });

    const primaryLabel = ts.primaryScore === 'game' ? tr('Board Pts') : tr('Match Pts');
    const secondaryLabel = ts.primaryScore === 'game' ? tr('Match Pts') : tr('Board Pts');

    return `
        <h2 class="text-center" style="margin-bottom: 1rem;">${tr('Team Standings after Round {n}', { n: state.rounds.length })}</h2>
        <table class="print-table">
            <thead>
                <tr>
                    <th style="width: 40pt;">${tr('Rank')}</th>
                    <th style="width: 30pt;" class="text-center">#</th>
                    <th>${tr('Team')}</th>
                    <th style="width: 45pt;" class="text-center">${tr('Pld')}</th>
                    <th style="width: 60pt;" class="text-right">${primaryLabel}</th>
                    <th style="width: 60pt;" class="text-right">${secondaryLabel}</th>
                </tr>
            </thead>
            <tbody>
                ${table.map(r => `
                    <tr>
                        <td class="text-center font-bold">${r.rank}</td>
                        <td class="text-center">${r.teamNo}</td>
                        <td>${escapeHtml(r.name || '')}</td>
                        <td class="text-center">${r.matchesPlayed}</td>
                        <td class="text-right font-bold">${r.primary}</td>
                        <td class="text-right">${ts.primaryScore === 'game' ? r.matchPoints : r.gamePoints}</td>
                    </tr>`).join('')}
            </tbody>
        </table>`;
}

/**
 * The printed ARENA team table: each team's players' points added up, the same table the
 * Standings tab shows (window.ArenaEngine.teamStandings). An Arena is ranked both ways, so
 * the individual list still follows it.
 */
function arenaTeamStandingsHtml() {
    const teams = getTeams();
    if (teams.length === 0) return '';
    const table = window.ArenaEngine.teamStandings(teams, state.players, state.rounds, getActiveSettings().pointSystem);

    return `
        <h2 class="text-center" style="margin-bottom: 1rem;">${tr('Team Standings after Round {n}', { n: state.rounds.length })}</h2>
        <table class="print-table">
            <thead>
                <tr>
                    <th style="width: 40pt;">${tr('Rank')}</th>
                    <th>${tr('Team')}</th>
                    <th style="width: 60pt;" class="text-center">${tr('Players')}</th>
                    <th style="width: 60pt;" class="text-right">${tr('Pts')}</th>
                    <th style="width: 60pt;" class="text-right">${tr('Average')}</th>
                </tr>
            </thead>
            <tbody>
                ${table.map(r => `
                    <tr>
                        <td class="text-center font-bold">${r.rank}</td>
                        <td>${escapeHtml(r.name)}</td>
                        <td class="text-center">${r.players}</td>
                        <td class="text-right font-bold">${r.points}</td>
                        <td class="text-right">${r.average.toFixed(2)}</td>
                    </tr>`).join('')}
            </tbody>
        </table>`;
}

export async function handlePrint(type) {
    if (!state.activeTournament) return;

    const printSection = document.getElementById('print-section');
    if (!printSection) return;

    let content = '';
    const tourney = state.activeTournament;
    // Printed sheets are the arbiter's own working documents, so a youth
    // tournament drops from them what it drops on screen for the same reason:
    // there is no rating calculation, so the rating change and the opponents'
    // average rating are arithmetic about nothing. Done by leaving the columns out
    // rather than hiding them, so the printed table has no empty gap.
    //
    // The BIRTH column stays. It is how the age classes get checked, on paper, at
    // the board — which is precisely the job in a children's tournament — and the
    // arbiter is the one holding the sheet.
    const youth = isYouthTournament(getActiveSettings());
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: '2-digit' });
    const fullTimeStr = now.toLocaleString('en-GB');

    // Common Header
    content += `
        <div class="print-header">
            <h1>${escapeHtml(tourney.name)}</h1>
            <p>${escapeHtml(tourney.location || tr('Location Not Set'))} • ${dateStr}</p>
        </div>
    `;

    if (type === 'players') {
        content += `<h2 class="text-center" style="margin-bottom: 1.5rem;">${tr('Tournament Participant List')}</h2>`;
        content += `
            <table class="print-table">
                <thead>
                    <tr>
                        <th style="width: 30pt;" class="text-center">#</th>
                        <th style="width: 40pt;">${tr('Title')}</th>
                        <th>${tr('Player Name')}</th>
                        <th style="width: 60pt;" class="text-right">${tr('Rating')}</th>
                        <th>${tr('Club / Team')}</th>
                        <th style="width: 60pt;">${tr('Birth')}</th>
                        <th style="width: 100pt;" class="text-right">${tr('FIDE ID')}</th>
                    </tr>
                </thead>
                <tbody>
                    ${state.players.sort((a, b) => a.lastName.localeCompare(b.lastName)).map(p => `
                        <tr>
                            <td class="text-center font-bold">${escapeHtml(p.startNo || '')}</td>
                            <td>${escapeHtml(p.title || '')}</td>
                            <td>${escapeHtml(`${p.lastName}, ${p.givenName}`)}</td>
                            <td class="text-right">${escapeHtml((p.rating && p.rating !== 0 && p.rating !== '0') ? p.rating : '')}</td>
                            <td>${escapeHtml(p.club || '')}</td>
                            <td>${escapeHtml(playerBirthYear(p))}</td>
                            <td class="text-right">${escapeHtml(p.fideId || '')}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
            <p class="font-bold">${tr('Total registered: {n} players', { n: state.players.length })}</p>
        `;
    } else if (type === 'pairings') {
        if (state.rounds.length === 0) return;
        const style = await choosePairingPrintStyle();
        if (!style) return;
        if (style === 'tickbox') {
            await printTickBoxPairings();
            return;
        }
        // While browsing a round robin schedule preview, viewedRoundIndex can point
        // past the generated rounds — print the latest generated round instead.
        const round = state.rounds[Math.min(state.viewedRoundIndex, state.rounds.length - 1)];

        // Need ranks for names
        const standings = SwissEngine.calculateStandings(state.players, state.rounds.slice(0, -1), ...rankingArgs());
        const rankMap = {};
        standings.forEach((s, idx) => { rankMap[s.id] = idx + 1; });

        // The round's start time goes on the sheet that gets pinned to the wall — that
        // sheet is where a player looks to find their board, and it is the natural place
        // to find out when to be at it.
        const when = roundWhenLabel(round.roundNo);
        content += `<h2 class="text-center" style="margin-bottom: ${when ? '0.25rem' : '1.5rem'};">${tr('Round {n}', { n: round.roundNo })}</h2>`;
        if (when) content += `<p class="text-center" style="margin: 0 0 1.5rem; font-size: 10pt;">${escapeHtml(when)}</p>`;

        // A team round is printed match by match — see teamPairingsHtml. Written as a
        // branch rather than an early return so the shared print footer below still runs.
        const isTeamRound = Array.isArray(round.matches) && round.matches.length > 0;
        content += isTeamRound ? teamPairingsHtml(round) : `
            <table class="print-table">
                <thead>
                    <tr>
                        <th style="width: 40pt;">${tr('Board')}</th>
                        <th>${tr('White')}</th>
                        <th style="width: 60pt;" class="text-center">${tr('Result')}</th>
                        <th style="width: 60pt;" class="text-center">${tr('Result')}</th>
                        <th>${tr('Black')}</th>
                    </tr>
                </thead>
                <tbody>
                    ${round.pairings.map((p, idx) => {
            const white = state.players.find(pl => pl.id === p.white);
            const black = p.black === 'WO' || p.black === 'BYE' ? { lastName: 'WO', givenName: '', id: 'WO' } : state.players.find(pl => pl.id === p.black);

            const resParts = ['', ''];
            if (p.result === '+/-') { resParts[0] = '1'; resParts[1] = '0'; }
            else if (p.result === '-/+') { resParts[0] = '0'; resParts[1] = '1'; }
            else if (p.result === '-/-') { resParts[0] = '0'; resParts[1] = '0'; }
            else if (p.result && p.result.includes('-')) {
                const parts = p.result.split('-');
                resParts[0] = parts[0];
                resParts[1] = parts[1];
            }

            return `
                            <tr>
                                <td class="text-center font-bold">${idx + 1}</td>
                                <td>${escapeHtml(formatPlayerName(white))}</td>
                                <td class="text-center font-bold">${resParts[0] || ''}</td>
                                <td class="text-center font-bold">${resParts[1] || ''}</td>
                                <td>${escapeHtml(formatPlayerName(black))}</td>
                            </tr>
                        `;
        }).join('')}
                </tbody>
            </table>
        `;
    } else if (type === 'crosstable') {
        // Built by js/ui/crosstable.js from the same ranking the screen shows, rather than
        // assembled again here — the printed sheet is the one that ends up on the wall, and
        // a second implementation of the same table is a second thing to keep in step.
        content += crosstablePrintHtml();
    } else if (type === 'prizes') {
        // Same reasoning as the crosstable above: built by the module that owns the view,
        // from the ranking the screen is showing, rather than assembled a second time here.
        content += prizesPrintHtml();
    } else if (type === 'standings') {
        // The team table decides the competition, so it leads. The individual list below
        // it is still worth printing: those are the board scores that get rated.
        const format = getActiveSettings().format;
        if (format === 'team' && window.TeamEngine) {
            content += teamStandingsHtml();
        } else if (format === 'arena' && window.ArenaEngine) {
            content += arenaTeamStandingsHtml();
        }

        const standings = SwissEngine.calculateStandings(state.players, state.rounds, ...rankingArgs());
        const roundNo = state.rounds.length;

        content += `<h2 class="text-center" style="margin-bottom: 1.5rem;">${tr('Preliminary Standings after Round {n}', { n: roundNo })}</h2>`;

        // Table Header with Round Numbers
        let roundHeaders = '';
        for (let i = 1; i <= roundNo; i++) {
            roundHeaders += `<th style="width: 25pt;" class="text-center">${i}</th>`;
        }

        content += `
            <table class="print-table">
                <thead>
                    <tr>
                        <th style="width: 40pt;">${tr('Rank')}</th>
                        <th>${tr('Player')}</th>
                        <th style="width: 50pt;" class="text-right">${tr('Rating')}</th>
                        <th>${tr('Club')}</th>
                        ${roundHeaders}
                        <th style="width: 40pt;" class="text-right">${tr('Pts')}</th>
                        <th style="width: 40pt;" class="text-right">BH-1</th>
                        <th style="width: 40pt;" class="text-right">BH</th>
                        ${youth ? '' : `<th style="width: 45pt;" class="text-right">${tr('AOR')}</th>`}
                        ${youth ? '' : '<th style="width: 40pt;" class="text-right">+/-</th>'}
                    </tr>
                </thead>
                <tbody>
                    ${standings.map((p, idx) => {
            let roundCells = '';
            const rankMap = {};
            standings.forEach((s, i) => { rankMap[s.id] = i + 1; });

            for (let i = 0; i < roundNo; i++) {
                const r = state.rounds[i];
                const pairing = r.pairings.find(pair => pair.white === p.id || pair.black === p.id);
                if (!pairing) {
                    roundCells += `<td class="text-center">-</td>`;
                } else {
                    const isWhite = pairing.white === p.id;
                    const oppId = isWhite ? pairing.black : pairing.white;
                    let oppRank = rankMap[oppId];
                    if (!oppRank && (oppId === 'WO' || oppId === 'BYE')) oppRank = oppId;
                    if (!oppRank) oppRank = '?';

                    let resultChar;
                    if (pairing.result === '1-0') resultChar = isWhite ? '+' : '-';
                    else if (pairing.result === '0-1') resultChar = isWhite ? '-' : '+';
                    else if (pairing.result === '0.5-0.5') resultChar = '=';
                    else if (pairing.result === '+/-') resultChar = isWhite ? '+' : '-';
                    else if (pairing.result === '-/+') resultChar = isWhite ? '-' : '+';
                    else if (pairing.result === '-/-') resultChar = '-';
                    else resultChar = '?';

                    const isBold = isWhite;
                    roundCells += `<td class="text-center ${isBold ? 'font-bold' : ''}">${resultChar}${oppRank}</td>`;
                }
            }

            return `
                            <tr>
                                <td class="text-center font-bold">${idx + 1}</td>
                                <td>${escapeHtml(formatPlayerName(p))}</td>
                                <td class="text-right">${escapeHtml((p.rating && p.rating !== 0 && p.rating !== '0') ? p.rating : '')}</td>
                                <td>${escapeHtml(p.club || '')}</td>
                                ${roundCells}
                                <td class="text-right font-bold">${p.points}</td>
                                <td class="text-right">${p.bh1.toFixed(1)}</td>
                                <td class="text-right">${p.bh.toFixed(1)}</td>
                                ${youth ? '' : `<td class="text-right">${p.aor > 0 ? Math.round(p.aor) : ''}</td>`}
                                ${youth ? '' : `<td class="text-right">${(() => {
                    if (!p.ratingChange || p.ratingChange === 0) return '';
                    const val = p.ratingChange.toFixed(1).replace('.', ',');
                    const sign = p.ratingChange > 0 ? '+' : '';
                    return sign + val;
                })()}</td>`}
                            </tr>
                        `;
        }).join('')}
                </tbody>
            </table>
            <div style="font-size: 9pt; margin-top: 1rem;">
                <p>1 ${tr('Points')}: ${tr('Total Points')}</p>
                <p>2 BH-1: ${tr('Buchholz -1 (worst result excluded)')}</p>
                <p>3 BH: ${tr('Buchholz (all results included)')}</p>
                ${youth ? '' : `<p>4 ${tr('AOR')}: ${tr('Average Opponent Rating')}</p>`}
                <p style="margin-top: 0.5rem;">${tr('White games are shown in <strong>bold</strong>.')}</p>
            </div>
        `;
    }

    content += `
        <div class="print-footer">
            <span>${tr('Printed on {time}', { time: fullTimeStr })} • Zugzwang</span>
            <span>${escapeHtml(tourney.club || '')}</span>
        </div>
    `;

    printSection.innerHTML = content;
    window.print();
}